/**
 * usePinnedModules Hook
 * Custom hook for managing pinned modules in the sidebar
 */

import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { toast } from 'react-toastify';
import {
  pinModule,
  unpinModule,
  reorderPinnedModules,
  clearPinnedModules,
  PinnedModule,
} from '../store/layouts/reducer';
import { PINNED_MODULES_CONFIG } from '../utils/constants';

// Selectors
const selectPinnedModules = (state: any) => state.Layout.pinnedModules;

const selectPinnedModulesData = createSelector(
  [selectPinnedModules],
  (pinnedModules) => pinnedModules || []
);

export interface UsePinnedModulesReturn {
  pinnedModules: PinnedModule[];
  isPinned: (moduleId: string) => boolean;
  canPinMore: boolean;
  maxPinnedItems: number;
  pinModuleAction: (module: PinnedModule) => void;
  unpinModuleAction: (moduleId: string) => void;
  togglePin: (module: PinnedModule) => void;
  reorderModules: (modules: PinnedModule[]) => void;
  clearAllPins: () => void;
}

export const usePinnedModules = (): UsePinnedModulesReturn => {
  const dispatch = useDispatch();
  const pinnedModules = useSelector(selectPinnedModulesData);

  const isPinned = useCallback(
    (moduleId: string): boolean => {
      return pinnedModules.some((m: PinnedModule) => m.id === moduleId);
    },
    [pinnedModules]
  );

  const canPinMore = useMemo(() => {
    return pinnedModules.length < PINNED_MODULES_CONFIG.MAX_PINNED_ITEMS;
  }, [pinnedModules]);

  const pinModuleAction = useCallback(
    (module: PinnedModule) => {
      if (isPinned(module.id)) {
        toast.info('Module is already pinned');
        return;
      }
      if (!canPinMore) {
        toast.warning(`Maximum ${PINNED_MODULES_CONFIG.MAX_PINNED_ITEMS} modules can be pinned`);
        return;
      }
      dispatch(pinModule(module));
      toast.success(`"${module.label}" pinned successfully`);
    },
    [dispatch, isPinned, canPinMore]
  );

  const unpinModuleAction = useCallback(
    (moduleId: string) => {
      const module = pinnedModules.find((m: PinnedModule) => m.id === moduleId);
      dispatch(unpinModule(moduleId));
      if (module) {
        toast.info(`"${module.label}" unpinned`);
      }
    },
    [dispatch, pinnedModules]
  );

  const togglePin = useCallback(
    (module: PinnedModule) => {
      if (isPinned(module.id)) {
        unpinModuleAction(module.id);
      } else {
        pinModuleAction(module);
      }
    },
    [isPinned, pinModuleAction, unpinModuleAction]
  );

  const reorderModules = useCallback(
    (modules: PinnedModule[]) => {
      dispatch(reorderPinnedModules(modules));
    },
    [dispatch]
  );

  const clearAllPins = useCallback(() => {
    dispatch(clearPinnedModules());
    toast.info('All pinned modules cleared');
  }, [dispatch]);

  return {
    pinnedModules,
    isPinned,
    canPinMore,
    maxPinnedItems: PINNED_MODULES_CONFIG.MAX_PINNED_ITEMS,
    pinModuleAction,
    unpinModuleAction,
    togglePin,
    reorderModules,
    clearAllPins,
  };
};

export default usePinnedModules;
